import { useEffect, useRef, useState } from "react"
import { observer } from "mobx-react-lite"

import type { StrategyManagerStore } from "@/domains/vote-optimizer"
import type { VoteOptimizationStrategyType } from "@/domains/vote-optimizer/vote-optimization-strategy-factory"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { LcAddress } from "@/domains/core"
import { formatTokenOptimizationValue } from "@/domains/library"
import type { TokenStore } from "@/domains/tokens"
import { SCALE, USDC, WETH, frxUSD } from "@/domains/tokens/static-tokens"
import { filterTokensBySearch } from "@/domains/tokens/utils/filter-tokens"

import { OptimizationEngineDropdownView } from "./optimization-engine-dropdown-view"

const FEATURED_TOKEN_STRATEGIES: Array<TokenStore> = [SCALE, WETH, USDC, frxUSD]

function tokenToOption(
  token: TokenStore,
  strategyManagerStore: StrategyManagerStore,
): {
  address: string
  symbol: string
  name: string
  imageURL: string
  displayValue: string
} {
  const estimatedAmount = strategyManagerStore.getTokenStrategyEstimatedAmount(token.address)
  return {
    address: token.address.toViemAddress(),
    symbol: token.symbol,
    name: token.name,
    imageURL: token.imageURL,
    displayValue: formatTokenOptimizationValue(estimatedAmount.toString(), token.decimals),
  }
}

export const OptimizationEngineDropdown = observer(() => {
  const scaleApp = useScaleApplication()
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const triggerRef = useRef<HTMLDivElement | null>(null)
  const dropdownRef = useRef<HTMLDivElement | null>(null)

  if (!scaleApp) {
    return null
  }

  const votePageStore = scaleApp.Pages.Vote.Store
  const strategyManagerStore = scaleApp.VoteOptimizer.Store

  const strategies = strategyManagerStore.allStrategyResults
  const allTokens = scaleApp.Tokens.Repository.getAll()

  const hasSearch = searchQuery.trim().length > 0
  const bribeTokens = allTokens.filter((t) => t.isIncentiveToken)
  const featuredAddresses = new Set(FEATURED_TOKEN_STRATEGIES.map((t) => t.address.toViemAddress()))
  const featuredBribeTokens = bribeTokens.filter((t) => featuredAddresses.has(t.address.toViemAddress()))
  const otherBribeTokens = bribeTokens.filter((t) => !featuredAddresses.has(t.address.toViemAddress()))

  const featuredTokenOptions = hasSearch
    ? filterTokensBySearch(featuredBribeTokens, searchQuery).map((t) => tokenToOption(t, strategyManagerStore))
    : featuredBribeTokens.map((t) => tokenToOption(t, strategyManagerStore))
  const searchableTokenOptions = hasSearch
    ? filterTokensBySearch(otherBribeTokens, searchQuery).map((t) => tokenToOption(t, strategyManagerStore))
    : []

  const selectedTokenAddress = votePageStore.selectedTokenStrategy?.toViemAddress() ?? null

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      const inTrigger = triggerRef.current?.contains(target)
      const inDropdown = dropdownRef.current?.contains(target)
      if (!inTrigger && !inDropdown) {
        setIsOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const handleSelectOptimizationStrategy = (id: VoteOptimizationStrategyType) => {
    votePageStore.applyStrategy(id, strategyManagerStore)
  }

  const handleSelectTokenStrategy = (address: string) => {
    votePageStore.applyTokenStrategy(new LcAddress(address as `0x${string}`), strategyManagerStore)
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  return (
    <OptimizationEngineDropdownView
      strategies={strategies.map((s) => ({
        id: s.id,
        label: s.label,
        estimatedValue: s.estimatedValue,
        icon: s.icon,
      }))}
      featuredTokens={featuredTokenOptions}
      searchableTokens={searchableTokenOptions}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      selectedEngineType={votePageStore.selectedEngineType}
      selectedOptimizationStrategy={votePageStore.selectedOptimizationStrategy}
      selectedTokenAddress={selectedTokenAddress}
      onSelectOptimizationStrategy={handleSelectOptimizationStrategy}
      onSelectTokenStrategy={handleSelectTokenStrategy}
      isOpen={isOpen}
      onToggle={() => setIsOpen((prev) => !prev)}
      onClickOutside={handleClose}
      triggerRef={triggerRef}
      dropdownRef={dropdownRef}
    />
  )
})
